import store from "../../store";
import { setProductId } from "../../store/slices/product/productSlice";
import {
  setCreateDetailsData,
  setData,
  setOrderId,
  setProposalFieldsJSON,
  setProposalJson,
  setProposalLayoutJSON,
  setProposalRulesJSON,
  setSummaryDetails,
  setUrl,
} from "../../store/slices/proposal/proposalSlice";
import {
  RenderPage,
  setAgName,
  setCustomerData,
  setEntityId,
  setIsFirstLogin,
  setLoading,
  setOrganizationId,
  setPersonalDetailState,
  setProfileJson,
  setRendererFormState,
  setRendererVersion,
  updateRendererFormState,
} from "../../store/slices/commonSlice";
import api from "../../utils/interface";
import { hoistNestedFiles } from "../../utils/craftFormFiles";
import { toast } from "react-toastify";

export const getCustomerInfo = async () => {
  store.dispatch(setLoading(true));
  try {
    api
      .get(`/agsp/customer/getCustomerInfo`)
      .then((res) => {
        if (res && res.status === 200) {
          const profileJson =
            typeof res.data.profile_json === "string"
              ? JSON.parse(res.data.profile_json)
              : res.data.profile_json;
          if (profileJson) {
            store.dispatch(updateRendererFormState(profileJson));
          } else {
            store.dispatch(updateRendererFormState(res.data));
          }
          store.dispatch(setProfileJson(profileJson));
          store.dispatch(setCustomerData(res.data));
          store.dispatch(setAgName(res.data.ag_name));
          store.dispatch(setOrganizationId(res.data.organizationid));
          store.dispatch(setEntityId(res.data.entityid));
          store.dispatch(setIsFirstLogin(res.data.is_first_login));
          store.dispatch(
            setPersonalDetailState(JSON.parse(res.data.profile_json).dn_state),
          );
          store.dispatch(setLoading(false));
        }
      })
      .catch((error) => {
        console.log(error);
        store.dispatch(setLoading(false));
      });
  } catch (error) {
    console.error("Error fetching data", error);
  }
};

export const getFormRenderer = async (productId, organizationId) => {
  const fd = new FormData();

  fd.append("parentId", productId);
  fd.append("parentType", "PPET");
  fd.append("mappedEntityType", "DPF");
  fd.append("organizationId", organizationId);

  try {
    await api.post(`/agsp/getViewMasterData`, fd).then((response) => {
      if (response && response.status === 200) {
        store.dispatch(setRendererFormState(response.data));
        // `renderer: 1` selects the @craft-form/renderer-driven navigation for
        // the proposal form's Previous/Next/Finish buttons.
        store.dispatch(setRendererVersion(response.data.renderer));
        const proposalLayoutJSON =
          typeof response.data.publishedformlayout === "string"
            ? JSON.parse(response.data.publishedformlayout)
            : response.data.publishedformlayout;

        const proposalFieldsJSON =
          typeof response.data.publishedformfields === "string"
            ? JSON.parse(response.data.publishedformfields)
            : response.data.publishedformfields;

        store.dispatch(setProposalFieldsJSON(proposalFieldsJSON));
        store.dispatch(setProposalLayoutJSON(proposalLayoutJSON));

        // `publishedrules` (not `publishedformrules`) — the column name on
        // cr_viewmaster; same key the admin panel reads. Guarded because it is
        // the one schema column with no draft counterpart and no publish-time
        // promotion, so it can legitimately be absent on a form whose layout
        // and fields are published.
        if (response.data?.publishedrules) {
          const proposalRulesJSON =
            typeof response.data.publishedrules === "string"
              ? JSON.parse(response.data.publishedrules)
              : response.data.publishedrules;

          store.dispatch(setProposalRulesJSON(proposalRulesJSON));
        }
      }
    });
  } catch (error) {
    console.error("Error fetching data", error);
  }
};

export const createTestPayment = async () => {
  try {
    const response = await fetch("https://sandbox.cashfree.com/pg/testorder", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": "YOUR_SANDBOX_CLIENT_ID",
        "x-client-secret": "YOUR_SANDBOX_CLIENT_SECRET",
      },
      body: JSON.stringify({
        order_id: "order_123",
        order_amount: 1000,
        order_currency: "INR",
        customer_name: "John Doe",
        customer_email: "john.doe@example.com",
        customer_phone: "9999999999",
        return_url: "https://localhost:3000",
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data.payment_id;
  } catch (error) {
    console.error("Error creating test payment:", error);
  }
};

export const createForm = async (
  entity,
  entityid,
  gross_amount,
  variantJson,
  entitytype,
  cover_amount,
  ag_id,
  successFunction,
) => {
  const fd = new FormData();
  const formattedEntity = {
    product_id: entityid,
    gross_amount: Number(gross_amount),
    variant_json: JSON.stringify(variantJson),
    status: "PAYMENT_PENDING",
    cover_amount: cover_amount,
    ag_id: ag_id,
  };

  fd.append("entity", JSON.stringify(formattedEntity));
  fd.append("entitytype", entitytype);

  try {
    await api.post(`/entity/create`, fd).then((response) => {
      if (response && response.status === 200) {
        store.dispatch(setCreateDetailsData(response.data));
        store.dispatch(setOrderId(response.data.entityid));
        store.dispatch(RenderPage());
        // const newUrl = new URL(window.location.href);
        // newUrl.searchParams.set("orderId", response.data.entityid);
        // window.history.replaceState(null, "", newUrl.toString());
        // console.log(response.data)
        successFunction(response.data);
      }
    });
  } catch (error) {
    console.error("Error fetching data", error);
    const errorCode = error?.response?.data?.error_code;
    if (["ERR_022", "ERR_023", "ERR_024", "ERR_025"].includes(errorCode)) {
      throw error;
    }
    toast.error(
      error?.response?.data?.error_message || "An error occurred",
      error,
    );
  }
};

export const readAll = async (entityid, entitytype, nav) => {
  const fd = new FormData();
  fd.append("entityid", entityid);
  fd.append("entitytype", entitytype);

  try {
    await api.post(`/entity/read`, fd).then((response) => {
      if (response && response.status === 200) {
        store.dispatch(setData(response.data));
        store.dispatch(setProductId(response.data.product_id));
        // const proposalJson =
        //   typeof response.data.proposal_form === "string"
        //     ? JSON.parse(response.data.proposal_form)
        //     : response.data.proposal_form;
        // if (proposalJson) {
        //   store.dispatch(updateRendererFormState(proposalJson));
        // } else {
        //   store.dispatch(updateRendererFormState(response.data));
        // }
        // store.dispatch(setProposalJson(proposalJson));
        store.dispatch(setOrderId(response.data.entityid));
      }
    });
  } catch (error) {
    if (error?.response?.data?.status === 400) {
      toast.error(error?.response?.data?.error_message, {
        toastId: "error-toast",
      });
      nav("/home");
    }
  }
};

// export const updateForm = async (entity, entityid, entitytype, status) => {
//   const excludedKeys = [
//     "entitytype",
//     "entityid",
//     "status",
//     "enterpriseid",
//     "organizationid",
//     "code",
//     "seq_no",
//     "created_by",
//     "modified_by",
//     "created_date",
//     "modified_date",
//     "customer_id",
//     "product_id",
//     "premium_amount",
//     "billing_member_id",
//     "billing_first_name",
//     "billing_last_name",
//     "billing_contact",
//     "billing_email",
//     "variant_json",
//     "prev_order_id",
//     "parent_order_id",
//     "order_type",
//     "proposal_form_id",
//     "prev_order_code",
//     "parent_order_code",
//   ];

//   // Prepare FormData
//   const fd = new FormData();

//   // Separate file data from other data
//   const nonFileData = {};
//   const fileData = {};

//   Object.keys(entity).forEach((key) => {
//     if (!excludedKeys.includes(key)) {
//       if (entity[key] instanceof File) {
//         // Directly use File objects for file data
//         fileData[key] = entity[key];
//       } else {
//         nonFileData[key] = entity[key];
//       }
//     }
//   });

//   const formattedEntity = {
//     product_proposal_form:
//       Object.keys(nonFileData).length === 0
//         ? null
//         : JSON.stringify(nonFileData),
//     status: status || undefined,
//   };

//   // Append non-file data as JSON object
//   fd.append("entity", JSON.stringify(formattedEntity));
//   fd.append("entityid", entityid);
//   fd.append("entitytype", entitytype);

//   // Append file data separately
//   Object.keys(fileData).forEach((key) => {
//     fd.append(key, fileData[key], fileData[key].name);
//   });

//   if (status) {
//     fd.append("status", status);
//   }

//   try {
//     const response = await api.post(`/agsp/order/updateOrder`, fd);
//     return response.data;
//   } catch (error) {
//     console.error("Error updating entity", error);
//     throw error;
//   }
// };

export const updateForm = async (
  entity,
  entityid,
  entitytype,
  entityParam = false,
  status,
  proposal_filled,
) => {
  const excludedKeys = [
    "entitytype",
    "entityid",
    "status",
    "enterpriseid",
    "organizationid",
    "code",
    "seq_no",
    "created_by",
    "modified_by",
    "created_date",
    "modified_date",
    "customer_id",
    "product_id",
    "gross_amount",
    "billing_member_id",
    "billing_first_name",
    "billing_last_name",
    "billing_contact",
    "billing_email",
    "variant_json",
    "prev_order_id",
    "parent_order_id",
    "order_type",
    "proposal_form_id",
    "prev_order_code",
    "parent_order_code",
  ];

  // Prepare FormData
  const fd = new FormData();
  let formattedEntity = {};
  const nonFileData = {};
  const fileData = {};

  if (entityParam) {
    formattedEntity = entity;
  } else {
    // Separate file data from other data
    Object.keys(entity).forEach((key) => {
      if (!excludedKeys.includes(key)) {
        if (entity[key] instanceof File || entity[key]?.type === "file") {
          // Handle file type (both when it's a File object or existing file information)
          fileData[key] = entity[key];
        } else {
          // A relation-table row's document is a `File` nested inside this
          // value's array, which JSON.stringify would flatten to `{}` — hoist
          // it out into its own multipart part first (see utils/craftFormFiles.js).
          nonFileData[key] = hoistNestedFiles(entity[key], key, fileData);
        }
      }
    });

    formattedEntity = {
      product_proposal_form:
        Object.keys(nonFileData).length === 0
          ? null
          : JSON.stringify(nonFileData),
      // status: status || undefined,
      proposal_filled: proposal_filled,
    };
  }

  // Append non-file data as JSON object
  fd.append("entity", JSON.stringify(formattedEntity));
  fd.append("entityid", entityid);
  fd.append("entitytype", entitytype);

  // Append file data separately (either as File object or existing file reference)
  Object.keys(fileData).forEach((key) => {
    const file = fileData[key];

    if (file instanceof File) {
      fd.append(key, file);
    } else if (file?.type === "file") {
      fd.append(key, file.media_id);
    }
  });

  try {
    const response = await api.post(`/agsp/order/updateOrder`, fd);
    if (response && response.status === 200) {
      return response.data;
    }
  } catch (error) {
    toast.error("Something went wrong");
    return null;
  }
};

export const summaryMap = async (orderId) => {
  try {
    const response = await api.post(
      `/agsp/order/summaryMap?orderId=${orderId}`,
    );
    if (response && response.status === 200) {
      store.dispatch(setSummaryDetails(response.data));
    }
  } catch (error) {
    console.log("error in summary API", error);
  }
};

export const paymentDetails = async (parentid, entity_type, parenttype) => {
  const fd = new FormData();
  const formattedEntity = {
    parentid: Number(parentid),
    parenttype: parenttype,
  };

  fd.append("entity", JSON.stringify(formattedEntity));
  fd.append("entity_type", entity_type);

  try {
    const response = await api.post(`/payment/payment-details`, fd);
    if (response && response.status === 200) {
      const paymentUrl = response.data.url;
      store.dispatch(setUrl(paymentUrl));
      window.location.assign(paymentUrl);
    }
  } catch (err) {
    const errorCode = err?.response?.data?.error_code;
    if (["ERR_029", "ERR_030"].includes(errorCode)) {
      throw err;
    }
    toast.error(err?.response?.data?.error_message || "An error occurred");
  }
};

// Pure fetcher backing useCraftDataProvider's CraftDataProvider (see
// pages/proposalForm/hook/useCraftDataProvider.js). Mirrors the live call in
// components/common/FormRendererV2/components/CustomSelect.jsx — the same
// endpoint and the same customerData parentType/parentId scoping every
// proposal-form select is populated from today — minus that component's redux
// and ProfileFieldsJSON coupling, which the new renderer's dataProvider
// replaces.
//
// `type` is the dropdown's entityType (SAL, STA, CTY, ...) — the same
// identifiers already used as `entityType` in selectedDetails/JSON.js's FIELDS
// and RULES_JSON. `ownerType`/`ownerValue` are only needed for dependent
// dropdowns (e.g. City depends on State) and are omitted for independent ones.
// Unlike CustomSelect's call, which hardcodes `type === "CTY"`, they're
// forwarded for ANY type, so a new dependent rule needs no change here.
export const fetchProposalDropdownOptions = async ({
  type,
  parentType,
  parentId,
  ownerType,
  ownerValue,
}) => {
  if (parentId === undefined || parentId === null) {
    // Fails loud instead of silently posting parentId="undefined" to the
    // backend — @craft-form/renderer catches this per-field rather than
    // crashing the whole form, so throwing here is safe.
    throw new Error(
      `fetchProposalDropdownOptions: missing parentId while fetching options for "${type}"`,
    );
  }

  const fd = new FormData();

  fd.append("type", type);
  fd.append("parentType", parentType);
  fd.append("parentId", parentId);

  if (ownerType && ownerValue !== undefined && ownerValue !== null) {
    fd.append("ownerType", ownerType);
    fd.append("ownerValue", ownerValue);
  }

  const response = await api.post(`agsp/customer/getAllDropdownListItems`, fd);
  const data = response.data;

  // Normalizes to @craft-form/renderer's Option shape ({ label, value }) —
  // the same fallback chain CustomSelect.jsx uses for this endpoint's raw
  // rows, since the backend response isn't already { label, value } pairs.
  if (!Array.isArray(data)) {
    console.warn(
      `fetchProposalDropdownOptions: unexpected response for "${type}"`,
      data,
    );
    return [];
  }

  return data.map((item) => ({
    value: item.value ?? item.key,
    label: item.label ?? item.value ?? item.key,
  }));
};

export const readPrposalData = (orderId, displayMode) => {
  store.dispatch(setLoading(true));
  let fd = new FormData();

  fd.append("orderId", orderId);
  fd.append("displayMode", displayMode);
  // fd.append("organizationId", organizationId);

  api
    .post(`/agsp/order/getProposalFormData`, fd, {})
    .then((res) => {
      // store.dispatch(updateRendererState(res.data));
      store.dispatch(updateRendererFormState(res.data));
      store.dispatch(setProposalJson(res.data));
      store.dispatch(setLoading(false));
    })
    .catch((err) => {
      console.log(err);
      store.dispatch(setLoading(false));
    });
};
